import {
  AIRun,
  applyAIEvent,
} from '../../../components/shared/brand/aiProgress/aiRun';

/**
 * The matter pipeline's PubSub envelope, folded into the shared AI run model.
 *
 * Matter creation is the longest AI flow in the product and the one people
 * actually sit and watch, so it renders `AIProgressPanel` from a real `AIRun`
 * rather than from the legacy `{step, status, messages[]}` adapter. Everything
 * the engine sends — the step, its message, its OPTIONAL `narration`, any
 * `facts` it reports, per-document rows, the percentage, the terminal state —
 * arrives through `normalizeAIEvent`, which is the only reason a narration
 * line will show the day the engine starts emitting one. The legacy
 * `progress` array is still maintained beside this for the two screens that
 * have not moved yet (`MatterDetails`, `AddMatterForm`).
 *
 * One frame needs handling the shared normalizer deliberately does not do:
 * the pipeline's opening `matter_initiated`, which announces the run's step
 * list. `_build_active_steps()` has already dropped whatever the matter's AI
 * toggles turned off, so its LENGTH is an honest total to count against — but
 * the steps themselves are still not drawn until the engine reports them, so
 * a step that never runs can never sit unfinished on screen.
 */

const flatten = (raw: any): Record<string, any> | null => {
  const envelope = Array.isArray(raw) ? raw[0] : raw;
  if (!envelope || typeof envelope !== 'object') return null;
  const inner =
    envelope.data && typeof envelope.data === 'object' ? envelope.data : {};
  return { ...envelope, ...inner };
};

const startMs = (value: any): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return null;
  const ms = /^\d+$/.test(value.trim())
    ? Number(value.trim())
    : new Date(value).getTime();
  return Number.isFinite(ms) && ms > 0 ? ms : null;
};

/** The opening frame that announces the run's real step list. */
export function isMatterRunInit(data: Record<string, any>): boolean {
  const subType = String(
    data.sub_type || data.event_name || data.eventType || data.event_type || '',
  ).toLowerCase();
  const status = String(data.status ?? '').toLowerCase();
  return (
    subType === 'matter_initiated' ||
    subType === 'matter.initiated' ||
    (status === 'started' && Array.isArray(data.steps))
  );
}

export function reduceMatterAIEvent(
  run: AIRun,
  raw: any,
  now: number = Date.now(),
): AIRun {
  const data = flatten(raw);
  if (!data) return run;

  if (isMatterRunInit(data)) {
    const announced = Array.isArray(data.steps) ? data.steps.length : 0;
    return {
      ...run,
      status: run.status === 'idle' ? 'connecting' : run.status,
      stepTotal: announced > 0 ? announced : run.stepTotal,
      startedAt: run.startedAt ?? startMs(data.startTime ?? data.start_time) ?? now,
    };
  }

  return applyAIEvent(run, raw);
}
